import { FC, useMemo } from 'react'
import { useFormState } from 'react-final-form'

import RFFFields from '../../react-final-form-fields'

import {
  composeValidators,
  getEndDateRangeValidator,
  getStartDateRangeValidator,
  validateRequired,
} from '../../../../util/validation'
import { nameofFactory, getPlaceholderTextFromLabel } from '../../../../util'

import {
  TrainingPersonRequirementsFormFieldsProps,
  ITrainingPersonRequirementsValues,
} from './training-person-requirements-form-types'
import { useTrainingGroupTemplatesForPersonQuery, useTrainingTypesQuery } from '../../../../services'

const nameof = nameofFactory<ITrainingPersonRequirementsValues>()

const trainingTypeLabel = 'Training Type(s)'
const effectiveStartDateLabel = 'Requirement Applies From'
const effectiveEndDateLabel = 'Requirement Applies Until'

const startDateValidator = getStartDateRangeValidator('effectiveEndDate')
const endDateValidator = getEndDateRangeValidator('effectiveStartDate')

const TrainingPersonRequirementsFormFields: FC<TrainingPersonRequirementsFormFieldsProps> = props => {
  const { disabled, isUpdateMode = false } = props

  const { values } = useFormState<ITrainingPersonRequirementsValues>()

  const { data: trainingGroupsRequirements } = useTrainingGroupTemplatesForPersonQuery(values?.personId)

  const { data: trainingTypes } = useTrainingTypesQuery({ activeOnly: true })

  // Build an array of training types with associatedTrainingGroups using trainingGroupsRequirements
  const typesWithAssociatedGroups = useMemo(() => {
    const types = trainingTypes ?? []
    const requirements = trainingGroupsRequirements ?? []

    const map = new Map<
      number,
      { id: number; displayName: string; associatedTrainingGroups: { id: number; displayName: string }[] }
    >()
    types.forEach(t =>
      map.set(t.id, {
        id: t.id,
        displayName: (t as any).displayName || (t as any).name || '',
        associatedTrainingGroups: [],
      }),
    )

    requirements.forEach((req: any) => {
      const entry = map.get(req.id)
      if (entry) {
        entry.associatedTrainingGroups.push({
          id: req.trainingGroup?.id,
          displayName: (req.trainingGroup?.displayName || req.trainingGroup?.id) + ' Reqs',
        })
      }
    })

    return Array.from(map.values())
  }, [trainingTypes, trainingGroupsRequirements])

  const typeOptions = useMemo(
    () =>
      typesWithAssociatedGroups?.map(type => ({
        label: type.displayName,
        value: type.id,
        parents:
          type.associatedTrainingGroups
            ?.slice()
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map(group => ({
              label: group.displayName,
              value: group.id,
            })) ?? [],
      })) ?? [],
    [typesWithAssociatedGroups],
  )

  const hasParentGroups = useMemo(
    () => typeOptions.some(option => option.parents && option.parents.length > 0),
    [typeOptions],
  )

  return (
    <>
      {!isUpdateMode && (
        <RFFFields.DualList
          name={nameof('trainingTypeIds')}
          label={trainingTypeLabel}
          unselectedTitle="Available Training Types"
          selectedTitle="Selected Training Types"
          options={typeOptions}
          disabled={disabled}
          hidden={isUpdateMode}
          canFilter
          filterPlaceholder="Filter by training type or group"
          displayParentGroups={hasParentGroups}
          unGroupedTitle="Other Types"
        />
      )}

      {isUpdateMode && (
        <>
          <RFFFields.DatePicker
            name={nameof('effectiveStartDate')}
            label={effectiveStartDateLabel}
            info="Date when this requirement starts for this person. Usually matches Start Date, but it can change if duties change."
            placeholderText={getPlaceholderTextFromLabel(effectiveStartDateLabel)}
            disabled={disabled}
            validate={composeValidators(validateRequired, startDateValidator)}
            isDateOnly
            shouldCloseOnSelect
            popperProps={{ strategy: 'fixed' }}
            showMonthDropdown
            showYearDropdown
          />

          <RFFFields.DatePicker
            name={nameof('effectiveEndDate')}
            label={effectiveEndDateLabel}
            info="Optional. Date when this requirement ends for this person. Leave blank if the requirement is ongoing."
            placeholderText={getPlaceholderTextFromLabel(effectiveEndDateLabel)}
            disabled={disabled}
            validate={endDateValidator}
            isDateOnly
            shouldCloseOnSelect
            popperProps={{ strategy: 'fixed' }}
            showMonthDropdown
            showYearDropdown
          />
        </>
      )}
    </>
  )
}

export default TrainingPersonRequirementsFormFields
